/* eslint-disable no-shadow */
import moment from 'moment';
import 'moment/locale/fr.js';

/**
 * Get a UTC timestamp in seconds for the provided date
 * @param date
 * @param format
 * @returns
 */
function getUTC(date = false, format = 'X', locale = "en") {
  moment.locale(locale);
  let newDate = null;
  if (date) {
    if (isNaN(date) || date instanceof Date) {
      newDate = moment.utc(date).format(format);
    } else {
      newDate = moment.utc(new Date(Number(date) * 1000)).format(format);
    }
  } else {
    newDate = moment.utc().format(format);
  }
  if (format === 'X') {
    return Number(newDate);
  }
  return newDate;
}

/**
 * Get the current start of the day at time 00:00:00 in UTC
 * @returns
 */
function getStartOfDay(date = false, format = 'X', locale = "en") {
  moment.locale(locale);
  let newDate = null;
  if (date) {
    if (isNaN(date) || date instanceof Date) {
      newDate = moment.utc(date).startOf('day').format(format);
    } else {
      newDate = moment.utc(new Date(Number(date) * 1000)).startOf('day').format(format);
    }
  } else {
    newDate = moment.utc().startOf('day').format(format);
  }
  if (format === 'X') {
    return Number(newDate);
  }
  return newDate;
}

/**
 * Get the current start of the month at time 00:00:00 in UTC
 * @returns
 */
function getStartOfMonth(date = false, format = 'X') {
  let newDate = null;
  if (date) {
    if (isNaN(date) || date instanceof Date) {
      newDate = moment.utc(date).startOf('month').format(format);
    } else {
      newDate = moment.utc(new Date(Number(date) * 1000)).startOf('month').format(format);
    }
  } else {
    newDate = moment.utc().startOf('month').format(format);
  }
  if (format === 'X') {
    return Number(newDate);
  }
  return newDate;
}

/**
 * Get the current end of the month at time 00:00:00 in UTC
 * @returns
 */
function getEndOfMonth(date = false, format = 'X') {
  let newDate = null;
  if (date) {
    if (isNaN(date) || date instanceof Date) {
      newDate = moment.utc(date).endOf('month').format(format);
    } else {
      newDate = moment.utc(new Date(Number(date) * 1000)).endOf('month').format(format);
    }
  } else {
    newDate = moment.utc().endOf('month').format(format);
  }
  if (format === 'X') {
    return Number(newDate);
  }
  return newDate;
}

/**
 * Get the current start of the day at time 00:00:00 in UTC
 * @returns
 */
function getEndOfDay(date = false, format = 'X') {
  let newDate = null;
  if (date) {
    if (isNaN(date) || date instanceof Date) {
      newDate = moment.utc(date).endOf('day').format(format);
    } else {
      newDate = moment.utc(new Date(Number(date) * 1000)).endOf('day').format(format);
    }
  } else {
    newDate = moment.utc().endOf('day').format(format);
  }
  if (format === 'X') {
    return Number(newDate);
  }
  return newDate;
}

/**
 * Show the provided date in the users default local timezone
 * @param date
 * @param format
 * @returns {string}
 */
function getLocalDisplay(date, format = 'll', locale = "en") {
  moment.locale(locale);
  let newDate = null;
  if (!date || isNaN(date) || date instanceof Date) {
    newDate = moment(date).local().format(format);
  } else {
    newDate = moment(new Date(Number(date) * 1000)).local().format(format);
  }
  if (format === 'X') {
    return Number(newDate);
  }
  return newDate;
}

/**
 * @param date
 * @param noFuture
 * @param noPast
 * @param mustBeFirstOfMonth
 * @returns {string|null}
 */
function validate(date, noFuture = true, noPast = false, mustBeFirstOfMonth = false, locale = "en") {
  if (!date || date === '') {
    return locale === 'en' ? 'Please enter a date: YYYY/MM/DD' : 'Veuillez entrer une date : AAAA/MM/JJ';
  }
  if (!moment(date, "YYYY/MM/DD", true)._isValid) {
    return locale === 'en' ? 'Invalid date' : 'Date invalide';
  }
  if (noFuture && !moment(date, "YYYY/MM/DD", true).isBefore()) {
    return locale === 'en' ? 'Date cannot be in the future.' : 'La date ne peut pas être dans le futur.';
  }
  if (noPast && !moment(date, "YYYY/MM/DD", true).isAfter()) {
    return locale === 'en' ? 'Date cannot be in the past.' : 'La date ne peut pas être dans le passé.';
  }
  if (mustBeFirstOfMonth) {
    const check = moment(date, "YYYY/MM/DD");
    if (check.format('DD') !== '01') {
      return locale === 'en' ? 'The date must be the first of the month.' : 'La date doit être le premier du mois.';
    }
  }
  return null;
}

/**
 * Get the amount of time from Now for the date
 * @param date
 * @returns {string}
 */
function fromNow(date) {
  let newDate = null;
  if (!date || isNaN(date) || date instanceof Date) {
    newDate = moment(date).local().fromNow();
  } else {
    newDate = moment(new Date(Number(date) * 1000)).local().fromNow();
  }
  return newDate;
}

/**
 * Add time to a date
 * @param date
 * @param amount
 * @param type
 * @param format
 * @returns {string|number}
 */
function getFutureDate(date, amount, type = 'months', format = 'X') {
  if (['days', 'months', 'years'].indexOf(type) === -1) {
    throw "Invalid type passed to getFutureDate";
  }
  let startDate = null;
  if (date) {
    if (isNaN(date) || date instanceof Date) {
      startDate = moment.utc(date).startOf('day');
    } else {
      startDate = moment.utc(new Date(Number(date) * 1000)).startOf('day');
    }
  } else {
    startDate = moment.utc().startOf('day');
  }
  const endDate = moment.utc(startDate).add(amount, type).startOf('day').format(format);
  if (format === 'X') {
    return Number(endDate);
  }
  return endDate;
}

/**
 * Add time to a date
 * @param date
 * @param amount
 * @param type
 * @param format
 * @returns {string|number}
 */
function getPastDate(date, amount, type = 'months', format = 'X', locale = "en") {
  moment.locale(locale);
  if (['days', 'months', 'years'].indexOf(type) === -1) {
    throw "Invalid type passed to getPastDate";
  }
  let startDate = null;
  if (date) {
    if (isNaN(date) || date instanceof Date) {
      startDate = moment.utc(date).startOf('day');
    } else {
      startDate = moment.utc(new Date(Number(date) * 1000)).startOf('day');
    }
  } else {
    startDate = moment.utc().startOf('day');
  }
  const endDate = moment.utc(startDate).subtract(amount, type).startOf('day').format(format);
  if (format === 'X') {
    return Number(endDate);
  }
  return endDate;
}

/**
 * @param date
 * @param format
 * @returns {*}
 */
function formatDateForDisplay(date, format = 'll', locale = "en") {
  return getUTC(date, format, locale);
}

/**
 * Date will be coming in as unix time / 1000
 * @param date
 * @returns {*}
 */
function formatIncomingDate(date) {
  return getUTC(date, "YYYY/MM/DD");
}

/**
 * @param date
 * @param format
 * @returns {string}
 */
function format(date, format = 'll') {
  return moment(date).format(format);
}

/**
 * Convert the provided date to the start of that day (12AM) - This is only used for Birth dates, Hire Dates etc.. where time is not included. We set the in value to a truthy value (1) so as not to create any issues when a value of 0, (01/01/1970) is passed.
 * @param date
 * @returns {*}
 */
function formatOutgoingDate(date) {
  let ready = getStartOfDay(date);
  if (ready === 0) {
    ready = 1;
  }
  return ready;
}
export { getUTC, getStartOfDay, getStartOfMonth, getEndOfDay, getLocalDisplay, validate, fromNow, getFutureDate, getPastDate, formatDateForDisplay, formatIncomingDate, formatOutgoingDate, getEndOfMonth, format };